import { useState, useEffect, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { Redirect, useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Play,
  Pause,
  Square,
  SkipForward,
  Plus,
  Lock,
  Eye,
  Trophy,
  Users,
  Wifi,
  WifiOff,
  Monitor,
  Volume2,
  VolumeX,
  ChevronRight,
  Keyboard,
  X,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQuizSocket, type QuizTeam, type LeaderboardEntry, type QuizQuestion, type QuizSession } from "@/hooks/useQuizSocket";
import { usePendingOrders } from "@/context/PendingOrdersContext";

// ─── Types ────────────────────────────────────────────────────────────────────

interface FullSession {
  id: number;
  quizId: number;
  joinCode: string;
  status: string;
  currentRoundId: number | null;
  currentQuestionId: number | null;
  questionStatus: string;
  timerStartedAt: string | null;
  timerDurationSeconds: number | null;
  timerPausedRemainingMs: number | null;
  leaderboardVisible: boolean;
  quiz: {
    id: number;
    title: string;
    hostName: string | null;
    venue: string | null;
    maxTeamSize: number | null;
    defaultTimerSeconds: number;
    rounds: Array<{
      id: number;
      title: string;
      roundNumber: number;
      questions: Array<{
        id: number;
        questionNumber: number;
        type: string;
        questionText: string;
        correctAnswer: string | null;
        points: number;
        timerSeconds: number | null;
        noTimer: boolean;
        hostNotes: string | null;
        imageUrl: string | null;
        options?: Array<{ id: number; optionText: string; isCorrect: boolean; sortOrder: number }>;
      }>;
    }>;
  };
  teams: QuizTeam[];
  answers: Array<{
    id: number;
    teamId: number;
    teamName: string;
    questionId: number;
    answerText: string | null;
    submittedAt: string;
    submittedAfterMs: number | null;
    markStatus: string;
    isCorrect: boolean | null;
    pointsAwarded: number;
  }>;
}

// ─── Timer display ────────────────────────────────────────────────────────────

function TimerDisplay({
  session,
  serverOffset,
}: {
  session: FullSession | null;
  serverOffset: number;
}) {
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    if (!session?.timerStartedAt || !session.timerDurationSeconds) {
      setRemaining(null);
      return;
    }
    if (session.timerPausedRemainingMs !== null) {
      setRemaining(Math.ceil(session.timerPausedRemainingMs / 1000));
      return;
    }

    function calc() {
      if (!session?.timerStartedAt || !session.timerDurationSeconds) return;
      const elapsed = (Date.now() + serverOffset) - new Date(session.timerStartedAt).getTime();
      const rem = Math.max(0, session.timerDurationSeconds * 1000 - elapsed);
      setRemaining(Math.ceil(rem / 1000));
    }
    calc();
    const id = setInterval(calc, 250);
    return () => clearInterval(id);
  }, [session?.timerStartedAt, session?.timerDurationSeconds, session?.timerPausedRemainingMs, serverOffset]);

  if (remaining === null) return null;
  const pct = session?.timerDurationSeconds ? (remaining / session.timerDurationSeconds) * 100 : 100;
  const danger = pct < 25;

  return (
    <div className="space-y-1">
      <div className={`text-5xl font-mono font-bold text-center ${danger ? "text-red-500" : "text-foreground"}`}>
        {remaining}
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${danger ? "bg-red-500" : "bg-primary"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Mark status pill ─────────────────────────────────────────────────────────

function MarkBadge({ status }: { status: string }) {
  const map: Record<string, { icon: React.ReactNode; cls: string }> = {
    auto_correct:      { icon: <CheckCircle className="h-3.5 w-3.5" />, cls: "text-green-600 bg-green-50" },
    manual_correct:    { icon: <CheckCircle className="h-3.5 w-3.5" />, cls: "text-green-600 bg-green-50" },
    auto_incorrect:    { icon: <XCircle className="h-3.5 w-3.5" />,    cls: "text-red-600 bg-red-50" },
    manual_incorrect:  { icon: <XCircle className="h-3.5 w-3.5" />,    cls: "text-red-600 bg-red-50" },
    suggested_correct: { icon: <AlertCircle className="h-3.5 w-3.5" />, cls: "text-yellow-600 bg-yellow-50" },
    needs_review:      { icon: <AlertCircle className="h-3.5 w-3.5" />, cls: "text-orange-600 bg-orange-50" },
    pending:           { icon: <Clock className="h-3.5 w-3.5" />,       cls: "text-muted-foreground bg-muted" },
  };
  const m = map[status] ?? map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${m.cls}`}>
      {m.icon}
    </span>
  );
}

// ─── Audio controls ───────────────────────────────────────────────────────────

function AudioControls({ quizId }: { quizId: number }) {
  const token = localStorage.getItem("ayrshire_token");
  const { data: assets = [] } = useQuery<Array<{ id: number; name: string; url: string; purpose: string }>>({
    queryKey: ["quiz-audio", quizId],
    queryFn: () => fetch(`/api/admin/quiz/${quizId}/audio`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
  });
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentUrl, setCurrentUrl] = useState<string | null>(null);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);

  function play(url: string) {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = url;
      audioRef.current.volume = muted ? 0 : volume;
      audioRef.current.play().catch(() => null);
      setPlaying(true);
      setCurrentUrl(url);
    }
  }

  function pauseAudio() {
    audioRef.current?.pause();
    setPlaying(false);
  }

  function resumeAudio() {
    audioRef.current?.play().catch(() => null);
    setPlaying(true);
  }

  function stopAudio() {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setCurrentUrl(null);
  }

  function restartAudio() {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => null);
      setPlaying(true);
    }
  }

  return (
    <div className="space-y-3">
      <audio
        ref={audioRef}
        onEnded={() => setPlaying(false)}
        onPause={() => setPlaying(false)}
        onPlay={() => setPlaying(true)}
      />
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={playing ? pauseAudio : resumeAudio} disabled={!currentUrl}>
          {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        <Button size="sm" variant="outline" onClick={stopAudio} disabled={!currentUrl}>
          <Square className="h-4 w-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={restartAudio} disabled={!currentUrl}>
          Restart
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            const m = !muted;
            setMuted(m);
            if (audioRef.current) audioRef.current.volume = m ? 0 : volume;
          }}
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </Button>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={volume}
          className="w-24"
          onChange={e => {
            const v = Number(e.target.value);
            setVolume(v);
            if (audioRef.current) audioRef.current.volume = v;
          }}
        />
      </div>
      <div className="space-y-1 max-h-40 overflow-y-auto">
        {assets.map(a => (
          <button
            key={a.id}
            type="button"
            onClick={() => play(a.url)}
            className={`w-full text-left px-3 py-2 rounded text-sm hover:bg-muted transition-colors ${currentUrl === a.url ? "bg-muted font-medium" : ""}`}
          >
            <span className="font-medium">{a.name}</span>
            <span className="ml-2 text-xs text-muted-foreground capitalize">{a.purpose}</span>
          </button>
        ))}
        {assets.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-2">No audio assets uploaded for this quiz</p>
        )}
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminQuizHost() {
  const { user, isLoading: authLoading } = useAuth();
  const params = useParams<{ sessionId: string }>();
  const sessionId = Number(params.sessionId);
  const { toast } = useToast();
  const token = localStorage.getItem("ayrshire_token");
  const qc = useQueryClient();

  const { connected, state, emit } = useQuizSocket(isNaN(sessionId) ? null : sessionId);
  const [serverOffset] = useState(0);
  const { setAlertsOn } = usePendingOrders();

  // Mute new-order beeps while the quiz host panel is open — no alarm sounds mid-quiz.
  useEffect(() => {
    setAlertsOn(false);
    return () => setAlertsOn(true);
  }, [setAlertsOn]);

  // When socket connects (or reconnects), join the host room so the admin
  // receives quiz:host:* events (answer_in, answer_count) in real time.
  useEffect(() => {
    if (connected && !isNaN(sessionId)) {
      emit("quiz:host:join", { sessionId });
    }
  }, [connected, sessionId, emit]);
  const [showFinishConfirm, setShowFinishConfirm] = useState(false);
  const [showRemoveTeam, setShowRemoveTeam] = useState<QuizTeam | null>(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showAudio, setShowAudio] = useState(false);
  const [activeTab, setActiveTab] = useState<"answers" | "leaderboard">("answers");

  // Polling fallback for session state
  const { data: session, refetch } = useQuery<FullSession>({
    queryKey: ["quiz-session", sessionId],
    enabled: !isNaN(sessionId) && user?.role === "admin",
    refetchInterval: connected ? 30_000 : 3_000,
    queryFn: () =>
      fetch(`/api/admin/quiz/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  const socketSession = state.session ?? session ?? null;
  const { data: answers = [], refetch: refetchAnswers } = useQuery<FullSession["answers"]>({
    queryKey: ["quiz-answers", sessionId, socketSession?.currentQuestionId],
    enabled: !isNaN(sessionId) && !!socketSession?.currentQuestionId,
    refetchInterval: connected ? 60_000 : 3_000,
    queryFn: () =>
      fetch(`/api/admin/quiz/sessions/${sessionId}/answers`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  const { data: leaderboard = [], refetch: refetchLeaderboard } = useQuery<LeaderboardEntry[]>({
    queryKey: ["quiz-leaderboard", sessionId],
    enabled: !isNaN(sessionId),
    queryFn: () =>
      fetch(`/api/admin/quiz/sessions/${sessionId}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()),
  });

  // Socket answers update
  const liveAnswers = state.answers.length > 0 ? state.answers : answers;
  const liveLeaderboard = state.leaderboard.length > 0 ? state.leaderboard : leaderboard;
  const liveSession = state.session ?? session ?? null;
  const teams = state.teams.length > 0 ? state.teams : (session?.teams ?? []);

  // Refetch REST answers when a new question is presented via socket
  useEffect(() => {
    if (state.lastEvent === "quiz:question") {
      refetchAnswers?.();
    }
  }, [state.lastEvent]);

  // Host actions
  function hostEmit(event: string, extra: Record<string, unknown> = {}) {
    emit(event, { sessionId, ...extra });
  }

  function startRound(roundId: number) {
    hostEmit("quiz:host:start_round", { roundId });
    setTimeout(() => { refetch(); }, 500);
  }

  function getPrevRound() {
    if (!session?.quiz?.rounds) return null;
    const rIdx = session.quiz.rounds.findIndex(r => r.id === currentRound?.id);
    return rIdx > 0 ? session.quiz.rounds[rIdx - 1] : null;
  }

  function goToPrevRound() {
    hostEmit("quiz:host:prev_round", {});
    setTimeout(() => { refetch(); }, 500);
  }

  function presentQuestion(questionId: number) {
    hostEmit("quiz:host:present", { questionId });
  }

  function startTimer() { hostEmit("quiz:host:timer_start"); }
  function pauseTimer() { hostEmit("quiz:host:timer_pause"); }
  function resumeTimer() { hostEmit("quiz:host:timer_resume"); }
  function addTime(seconds: number) { hostEmit("quiz:host:timer_add", { seconds }); }
  function lockAnswers() { hostEmit("quiz:host:lock_answers"); }
  function revealAnswer() { hostEmit("quiz:host:reveal_answer"); }
  function revealLeaderboard() {
    hostEmit("quiz:host:reveal_leaderboard");
    setActiveTab("leaderboard");
    setTimeout(() => refetchLeaderboard(), 500);
  }
  function hideLeaderboard() {
    hostEmit("quiz:host:hide_leaderboard");
  }
  function pauseQuiz() { hostEmit("quiz:host:pause_quiz"); }
  function resumeQuiz() { hostEmit("quiz:host:resume_quiz"); }

  function finishQuiz() {
    hostEmit("quiz:host:finish");
    setShowFinishConfirm(false);
    setTimeout(() => { refetch(); refetchLeaderboard(); }, 1000);
  }

  async function markAnswer(answerId: number, isCorrect: boolean) {
    emit("quiz:host:mark_answer", { sessionId, answerId, isCorrect });
    setTimeout(() => refetchAnswers(), 300);
  }

  function adjustScore(teamId: number, points: number) {
    emit("quiz:host:adjust_score", { sessionId, teamId, points });
  }

  function removeTeam(teamId: number) {
    emit("quiz:host:remove_team", { sessionId, teamId });
    setShowRemoveTeam(null);
  }

  // Current round/question from session
  const currentRound = session?.quiz?.rounds?.find(r => r.id === liveSession?.currentRoundId) ?? null;
  const currentQuestion = currentRound?.questions.find(q => q.id === liveSession?.currentQuestionId) ?? null;
  const qStatus = liveSession?.questionStatus ?? "idle";

  // Find next question/round
  function getNextQuestion() {
    if (!currentRound || !currentQuestion) return null;
    const qIdx = currentRound.questions.findIndex(q => q.id === currentQuestion.id);
    return currentRound.questions[qIdx + 1] ?? null;
  }

  function getNextRound() {
    if (!session?.quiz?.rounds) return null;
    const rIdx = session.quiz.rounds.findIndex(r => r.id === currentRound?.id);
    return session.quiz.rounds[rIdx + 1] ?? null;
  }

  function handleNext() {
    const nextQ = getNextQuestion();
    if (nextQ) {
      presentQuestion(nextQ.id);
    } else {
      const nextR = getNextRound();
      if (nextR) {
        startRound(nextR.id);
      } else {
        toast({ title: "Last question of the quiz. Click FINISH to end." });
      }
    }
  }

  // Keyboard shortcuts — use refs so the handler always sees the latest values
  // without needing to re-register on every render.
  const qStatusRef = useRef(qStatus);
  qStatusRef.current = qStatus;
  const liveSessionStatusRef = useRef(liveSession?.status);
  liveSessionStatusRef.current = liveSession?.status;

  // Stable refs for action functions so the event handler never goes stale.
  const pauseTimerRef = useRef(pauseTimer);
  pauseTimerRef.current = pauseTimer;
  const resumeTimerRef = useRef(resumeTimer);
  resumeTimerRef.current = resumeTimer;
  const startTimerRef = useRef(startTimer);
  startTimerRef.current = startTimer;
  const handleNextRef = useRef(handleNext);
  handleNextRef.current = handleNext;
  const revealLeaderboardRef = useRef(revealLeaderboard);
  revealLeaderboardRef.current = revealLeaderboard;
  const pauseQuizRef = useRef(pauseQuiz);
  pauseQuizRef.current = pauseQuiz;
  const resumeQuizRef = useRef(resumeQuiz);
  resumeQuizRef.current = resumeQuiz;

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (document.activeElement?.tagName === "INPUT" || document.activeElement?.tagName === "TEXTAREA") return;
      if (e.key === " " || e.key === "Spacebar") {
        e.preventDefault();
        if (qStatusRef.current === "timer_running") pauseTimerRef.current();
        else if (qStatusRef.current === "timer_paused") resumeTimerRef.current();
        else if (qStatusRef.current === "presented") startTimerRef.current();
      }
      if (e.key === "ArrowRight") handleNextRef.current();
      if (e.key === "l" || e.key === "L") revealLeaderboardRef.current();
      if (e.key === "p" || e.key === "P") {
        if (liveSessionStatusRef.current === "paused") resumeQuizRef.current();
        else pauseQuizRef.current();
      }
      if (e.key === "?") setShowShortcuts(v => !v);
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []); // registered once — all values accessed via refs

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  const isLobby = liveSession?.status === "lobby" || !liveSession?.status;
  const isLive = liveSession?.status === "live";
  const isPaused = liveSession?.status === "paused";
  const isFinished = liveSession?.status === "completed" || liveSession?.status === "cancelled";

  const answeredCount = state.answeredCount > 0 ? state.answeredCount : liveAnswers.length;
  const totalTeams = state.totalTeams > 0 ? state.totalTeams : teams.length;

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Top bar */}
      <div className="bg-slate-800 border-b border-slate-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
          <Link href="/admin/quiz">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white gap-1">
              ← Back
            </Button>
          </Link>

          <div className="flex-1 min-w-0">
            <h1 className="font-bold text-lg truncate">{session?.quiz.title ?? "Quiz Host"}</h1>
          </div>

          {/* Join code */}
          <div className="bg-slate-700 rounded-lg px-4 py-2 text-center">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Join Code</p>
            <p className="text-2xl font-mono font-bold text-yellow-400">{liveSession?.joinCode ?? "—"}</p>
          </div>

          {/* Status */}
          <div className="flex items-center gap-2">
            {connected ? (
              <span className="flex items-center gap-1 text-green-400 text-sm"><Wifi className="h-4 w-4" /> Live</span>
            ) : (
              <span className="flex items-center gap-1 text-red-400 text-sm"><WifiOff className="h-4 w-4" /> Offline</span>
            )}
          </div>

          {/* Teams online */}
          <div className="flex items-center gap-1 text-sm">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="font-semibold">{teams.length}</span>
            <span className="text-slate-400">teams</span>
          </div>

          {/* Session status badge */}
          <Badge className={
            isLobby ? "bg-yellow-500 text-black" :
            isLive ? "bg-green-500 text-black" :
            isPaused ? "bg-orange-500 text-black" :
            isFinished ? "bg-purple-500" : "bg-slate-600"
          }>
            {liveSession?.status ?? "lobby"}
          </Badge>

          <Button
            size="sm"
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-700 gap-1"
            onClick={() => window.open(`/admin/quiz/projector/${sessionId}`, "_blank")}
          >
            <Monitor className="h-4 w-4" /> Projector
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="text-slate-400"
            onClick={() => setShowShortcuts(true)}
          >
            <Keyboard className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Main controls */}
        <div className="lg:col-span-2 space-y-4">

          {/* Lobby state */}
          {isLobby && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Lobby — Waiting for teams</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Quiz meta */}
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-400">
                  {session?.quiz?.hostName && (
                    <span>Hosted by <span className="text-white font-medium">{session.quiz.hostName}</span></span>
                  )}
                  {session?.quiz?.maxTeamSize && (
                    <span>Max team size: <span className="text-white font-medium">{session.quiz.maxTeamSize} players</span></span>
                  )}
                  {session?.quiz?.venue && (
                    <span>Venue: <span className="text-white font-medium">{session.quiz.venue}</span></span>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {teams.map(team => (
                    <div key={team.id} className="bg-slate-700 rounded-lg px-3 py-2 flex items-center gap-2">
                      <span className="text-lg">{team.emoji ?? "🎯"}</span>
                      <span className="text-sm font-medium text-white truncate">{team.name}</span>
                    </div>
                  ))}
                  {teams.length === 0 && (
                    <p className="text-slate-500 text-sm col-span-full py-2">No teams yet — share the join code above</p>
                  )}
                </div>
                {session?.quiz?.rounds?.[0] && (
                  <Button
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-500 text-white text-lg font-bold"
                    onClick={() => startRound(session.quiz.rounds[0].id)}
                    disabled={teams.length === 0}
                  >
                    <Play className="h-5 w-5 mr-2" />
                    {teams.length === 0 ? "Waiting for teams..." : `START QUIZ (${teams.length} teams)`}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Live/Question state */}
          {(isLive || isPaused) && (
            <>
              {/* Round/Question info */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-slate-400 mb-2">
                    {currentRound && (
                      <span>Round {currentRound.roundNumber}: {currentRound.title}</span>
                    )}
                    {currentQuestion && (
                      <>
                        <ChevronRight className="h-4 w-4" />
                        <span>Q{currentQuestion.questionNumber} of {currentRound?.questions.length ?? 0}</span>
                      </>
                    )}
                  </div>

                  {currentQuestion ? (
                    <div className="space-y-3">
                      <p className="text-white text-xl font-semibold leading-snug">{currentQuestion.questionText}</p>
                      {currentQuestion.imageUrl && (
                        <img src={currentQuestion.imageUrl} alt="" className="max-h-48 rounded object-contain" />
                      )}
                      {currentQuestion.hostNotes && (
                        <div className="bg-yellow-900/30 border border-yellow-700 rounded p-2 text-sm text-yellow-200">
                          <p className="text-xs font-semibold text-yellow-400 mb-0.5">HOST NOTES</p>
                          {currentQuestion.hostNotes}
                        </div>
                      )}
                      {(() => {
                        const correctOpts = currentQuestion.options?.filter(o => o.isCorrect).map(o => o.optionText) ?? [];
                        const correctText = correctOpts.length > 0
                          ? correctOpts.join(" / ")
                          : (currentQuestion.correctAnswer ?? null);
                        if (qStatus === "revealed" || qStatus === "locked") {
                          return correctText ? (
                            <div className="bg-green-900/30 border border-green-700 rounded p-2">
                              <p className="text-xs font-semibold text-green-400 mb-0.5">CORRECT ANSWER</p>
                              <p className="text-white font-bold text-lg">{correctText}</p>
                            </div>
                          ) : null;
                        }
                        return (
                          <div className="bg-slate-700 rounded p-2">
                            <p className="text-xs font-semibold text-slate-400 mb-0.5">CORRECT ANSWER (host only)</p>
                            <p className="text-white font-bold">{correctText ?? "—"}</p>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-slate-400">No question selected.</p>
                      {currentRound?.questions[0] && (
                        <Button onClick={() => presentQuestion(currentRound.questions[0].id)} className="gap-1">
                          <Play className="h-4 w-4" /> Present Q1
                        </Button>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Timer + Controls */}
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 space-y-4">
                  {/* Timer */}
                  {(qStatus === "timer_running" || qStatus === "timer_paused") && liveSession && (
                    <TimerDisplay session={liveSession as unknown as FullSession} serverOffset={serverOffset} />
                  )}

                  {/* Answer count */}
                  {qStatus !== "idle" && (
                    <div className="text-center">
                      <span className="text-2xl font-bold text-white">{answeredCount}</span>
                      <span className="text-slate-400"> / {totalTeams} teams answered</span>
                    </div>
                  )}

                  {/* Control buttons */}
                  <div className="flex flex-wrap gap-2 justify-center">
                    {qStatus === "idle" && currentRound && currentRound.questions.length > 0 && (
                      <Button
                        onClick={() => presentQuestion(currentRound.questions[0].id)}
                        className="bg-blue-600 hover:bg-blue-500 gap-1"
                      >
                        <Eye className="h-4 w-4" /> Present Question
                      </Button>
                    )}
                    {qStatus === "presented" && (
                      <Button onClick={startTimer} className="bg-green-600 hover:bg-green-500 gap-1">
                        <Play className="h-4 w-4" /> Start Timer
                      </Button>
                    )}
                    {qStatus === "timer_running" && (
                      <>
                        <Button onClick={pauseTimer} variant="outline" className="border-slate-600 text-white gap-1">
                          <Pause className="h-4 w-4" /> Pause
                        </Button>
                        <Button onClick={() => addTime(30)} variant="outline" className="border-slate-600 text-white gap-1">
                          <Plus className="h-4 w-4" /> +30s
                        </Button>
                      </>
                    )}
                    {qStatus === "timer_paused" && (
                      <Button onClick={resumeTimer} className="bg-green-600 hover:bg-green-500 gap-1">
                        <Play className="h-4 w-4" /> Resume
                      </Button>
                    )}
                    {(qStatus === "timer_running" || qStatus === "timer_paused" || qStatus === "presented") && (
                      <Button onClick={lockAnswers} className="bg-orange-600 hover:bg-orange-500 gap-1">
                        <Lock className="h-4 w-4" /> Lock Answers
                      </Button>
                    )}
                    {qStatus === "locked" && (
                      <Button onClick={revealAnswer} className="bg-purple-600 hover:bg-purple-500 gap-1">
                        <Eye className="h-4 w-4" /> Reveal Answer
                      </Button>
                    )}
                    {(qStatus === "revealed" || qStatus === "locked") && (
                      <>
                        {liveSession?.leaderboardVisible ? (
                          <Button onClick={hideLeaderboard} variant="outline" className="border-amber-500 text-amber-400 gap-1">
                            <Trophy className="h-4 w-4" /> Hide Leaderboard
                          </Button>
                        ) : (
                          <Button onClick={revealLeaderboard} variant="outline" className="border-slate-600 text-white gap-1">
                            <Trophy className="h-4 w-4" /> Leaderboard
                          </Button>
                        )}
                        {getPrevRound() && !getNextQuestion() && (
                          <Button onClick={goToPrevRound} variant="outline" className="border-slate-600 text-slate-300 gap-1" title="Go back to previous round">
                            ← Prev Round
                          </Button>
                        )}
                        <Button onClick={handleNext} className="bg-primary gap-1">
                          <SkipForward className="h-4 w-4" />
                          {getNextQuestion() ? "Next Question" : getNextRound() ? "Next Round" : "Finish"}
                        </Button>
                      </>
                    )}
                  </div>

                  {/* Pause/Resume quiz + Finish */}
                  <div className="flex gap-2 justify-center border-t border-slate-700 pt-3">
                    {isLive && (
                      <Button variant="outline" className="border-slate-600 text-slate-300 gap-1" onClick={pauseQuiz}>
                        <Pause className="h-4 w-4" /> Pause Quiz
                      </Button>
                    )}
                    {isPaused && (
                      <Button className="bg-green-600 gap-1" onClick={resumeQuiz}>
                        <Play className="h-4 w-4" /> Resume Quiz
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      className="border-red-800 text-red-400 hover:bg-red-900/30 gap-1"
                      onClick={() => setShowFinishConfirm(true)}
                    >
                      <Square className="h-4 w-4" /> FINISH QUIZ
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Question navigation */}
              {currentRound && (
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-300">Questions — {currentRound.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {currentRound.questions.map(q => {
                        const isCurrent = q.id === currentQuestion?.id;
                        return (
                          <button
                            key={q.id}
                            type="button"
                            onClick={() => presentQuestion(q.id)}
                            className={`w-9 h-9 rounded text-sm font-bold transition-colors ${isCurrent ? "bg-primary text-white" : "bg-slate-700 text-slate-300 hover:bg-slate-600"}`}
                          >
                            {q.questionNumber}
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Finished state */}
          {isFinished && (
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6 text-center space-y-4">
                <Trophy className="h-12 w-12 text-yellow-400 mx-auto" />
                <h2 className="text-2xl font-bold text-white">Quiz Complete!</h2>
                {liveLeaderboard[0] && (
                  <p className="text-xl text-yellow-400 font-semibold">
                    Winner: {liveLeaderboard[0].emoji} {liveLeaderboard[0].name} — {liveLeaderboard[0].score} pts
                  </p>
                )}
                <Link href={`/admin/quiz/history`}>
                  <Button variant="outline" className="border-slate-600 text-white">View Results</Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Audio panel */}
          {session?.quiz.id && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader
                className="pb-2 cursor-pointer"
                onClick={() => setShowAudio(v => !v)}
              >
                <CardTitle className="text-sm text-slate-300 flex items-center justify-between">
                  <span className="flex items-center gap-2"><Volume2 className="h-4 w-4" /> Audio Controls</span>
                  {showAudio ? <ChevronRight className="h-4 w-4 rotate-90" /> : <ChevronRight className="h-4 w-4" />}
                </CardTitle>
              </CardHeader>
              {showAudio && (
                <CardContent>
                  <AudioControls quizId={session.quiz.id} />
                </CardContent>
              )}
            </Card>
          )}
        </div>

        {/* Right: Teams, answers, leaderboard */}
        <div className="space-y-4">
          {/* Tab switcher */}
          <div className="flex bg-slate-800 rounded-lg p-1 gap-1">
            {(["answers", "leaderboard"] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-1.5 text-sm font-semibold rounded transition-colors capitalize ${activeTab === tab ? "bg-primary text-white" : "text-slate-400 hover:text-white"}`}
              >
                {tab}
              </button>
            ))}
          </div>

          {activeTab === "answers" && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-slate-300">
                  Answers {liveAnswers.length > 0 && `(${liveAnswers.length})`}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {liveAnswers.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-6">No answers yet</p>
                ) : (
                  <div className="divide-y divide-slate-700 max-h-[60vh] overflow-y-auto">
                    {liveAnswers.map((a, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2.5">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-slate-400 font-medium truncate">{a.teamName}</p>
                          <p className="text-sm text-white truncate">{a.answerText ?? "—"}</p>
                          {a.submittedAfterMs && (
                            <p className="text-xs text-slate-500">{(a.submittedAfterMs / 1000).toFixed(1)}s</p>
                          )}
                        </div>
                        <MarkBadge status={a.markStatus} />
                        {(a.markStatus === "pending" || a.markStatus === "needs_review" || a.markStatus === "suggested_correct") && a.id && (
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => markAnswer(a.id!, true)}
                              className="p-1 rounded text-green-400 hover:bg-green-900/30"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => markAnswer(a.id!, false)}
                              className="p-1 rounded text-red-400 hover:bg-red-900/30"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {activeTab === "leaderboard" && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm text-slate-300">Leaderboard</CardTitle>
                  {liveSession?.leaderboardVisible ? (
                    <Button size="sm" variant="outline" className="border-amber-500 text-amber-400 h-7 text-xs gap-1" onClick={hideLeaderboard}>
                      <Eye className="h-3.5 w-3.5" /> Hide from players
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="border-slate-600 text-slate-300 h-7 text-xs gap-1" onClick={revealLeaderboard}>
                      <Eye className="h-3.5 w-3.5" /> Show to players
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {liveLeaderboard.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-6">No scores yet</p>
                ) : (
                  <div className="divide-y divide-slate-700 max-h-[60vh] overflow-y-auto">
                    {liveLeaderboard.map(entry => (
                      <div key={entry.teamId} className="flex items-center gap-2 px-3 py-2.5">
                        <span className={`text-base font-bold w-7 text-center ${entry.rank === 1 ? "text-yellow-400" : entry.rank === 2 ? "text-slate-300" : entry.rank === 3 ? "text-amber-600" : "text-slate-500"}`}>
                          {entry.rank === 1 ? "🥇" : entry.rank === 2 ? "🥈" : entry.rank === 3 ? "🥉" : entry.rank}
                        </span>
                        <span className="text-base">{entry.emoji ?? ""}</span>
                        <span className="flex-1 text-sm text-white font-medium truncate">{entry.name}</span>
                        {entry.change !== undefined && entry.change !== 0 && (
                          <span className={`text-xs font-semibold ${entry.change > 0 ? "text-green-400" : "text-red-400"}`}>
                            {entry.change > 0 ? "↑" : "↓"}{Math.abs(entry.change)}
                          </span>
                        )}
                        <span className="text-white font-bold w-8 text-right">{entry.score}</span>
                        <button
                          type="button"
                          onClick={() => adjustScore(entry.teamId, -1)}
                          className="p-1 rounded text-red-400 hover:bg-red-900/30 text-xs font-bold"
                          title="Deduct 1 point"
                        >−1</button>
                        <button
                          type="button"
                          onClick={() => adjustScore(entry.teamId, 1)}
                          className="p-1 rounded text-green-400 hover:bg-green-900/30 text-xs font-bold"
                          title="Add 1 point"
                        >+1</button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Teams list */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm text-slate-300">Teams ({teams.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-700 max-h-48 overflow-y-auto">
                {teams.map(team => (
                  <div key={team.id} className="flex items-center gap-2 px-3 py-2">
                    <span>{team.emoji ?? "🎯"}</span>
                    <span className="flex-1 text-sm text-white truncate">{team.name}</span>
                    {team.isDisqualified && <Badge className="bg-red-900 text-red-200 text-xs">DQ</Badge>}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                      onClick={() => setShowRemoveTeam(team)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Keyboard shortcuts dialog */}
      <AlertDialog open={showShortcuts} onOpenChange={setShowShortcuts}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Keyboard Shortcuts</AlertDialogTitle>
          </AlertDialogHeader>
          <div className="space-y-2 text-sm">
            {[
              ["Space", "Start / pause timer"],
              ["→", "Next question / round"],
              ["L", "Reveal leaderboard"],
              ["P", "Pause / resume quiz"],
              ["?", "Toggle this help"],
            ].map(([key, desc]) => (
              <div key={key} className="flex items-center gap-3">
                <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">{key}</kbd>
                <span className="text-muted-foreground">{desc}</span>
              </div>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowShortcuts(false)}>Close</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Remove team confirm */}
      <AlertDialog open={!!showRemoveTeam} onOpenChange={open => !open && setShowRemoveTeam(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove "{showRemoveTeam?.name}" from the quiz? They will be disconnected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => showRemoveTeam && removeTeam(showRemoveTeam.id)}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Finish quiz confirm */}
      <AlertDialog open={showFinishConfirm} onOpenChange={setShowFinishConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finish Quiz?</AlertDialogTitle>
            <AlertDialogDescription>
              This will end the quiz session. Final scores will be locked and the results saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={finishQuiz}
            >
              Finish Quiz
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
